<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="statistics-manage">
    <div class="filter-bar" style="justify-content: space-between;">
      <div>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日"
          end-placeholder="结束日"
          value-format="YYYY-MM-DD"
          size="default"
          style="width: 220px"
        />
        <el-button type="primary" style="margin-left: 10px;" @click="getRevenue">查询</el-button>
      </div>
      <div>
        <el-radio-group v-model="periodUnit" size="default" @change="unitChange">
          <el-radio-button value="day">日</el-radio-button>
          <el-radio-button value="month">月</el-radio-button>
          <el-radio-button value="year">年</el-radio-button>
        </el-radio-group>
      </div>
    </div>

    <!-- 柱状图区域 -->
    <div class="chart-area">
      <div ref="barChartRef" class="bar-chart"></div>
    </div>

    <!-- 饼图弹窗 -->
    <el-dialog
      v-model="pieDialogVisible"
      :title="`${selectedBar} 菜品销售占比`"
      width="600px"
      destroy-on-close
    >
      <div class="pie-toolbar">
        <el-radio-group v-model="pieDataType" size="small" @change="onPieDataTypeChange">
          <el-radio-button value="amount">销售额占比</el-radio-button>
          <el-radio-button value="quantity">数量占比</el-radio-button>
        </el-radio-group>
      </div>
      <div ref="pieChartRef" class="pie-chart"></div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import * as echarts from 'echarts'
import { ElMessage } from 'element-plus'
import { useStatisticsStore } from '@/stores/admin/statisticsStore'
import tzDayjs from '@/utils/dayjs'

const statisticsStore = useStatisticsStore()

const dateRange = ref([tzDayjs().subtract(7, 'day').format('YYYY-MM-DD'), tzDayjs.getCurrentDate()])
const periodUnit = ref('day')
const selectedBar = ref('')

// 柱状图数据
let barChart = null
const barChartRef = ref(null)
const barData = computed(()=>{
  let result = [];
  for( let [key, value] of statisticsStore.periodRef){
    result.push({
      xItem: key,
      yItem: value.totalRevenue
    })
  }
  return result;
})

// 饼图dialog
const pieDialogVisible = ref(false)
// 饼图显示单位
const pieDataType = ref('')

// 饼图数据详情
let pieChart = null
const pieChartRef = ref(null)
const pieData = computed(() => {
  const data = statisticsStore.periodRef.get(selectedBar.value)
  if(!data) return []
  return data.dishes.map(dish => ({
    name : dish.name, value: pieDataType.value === 'quantity' ? dish.quantity : dish.price * dish.quantity
  }) )
})

// 渲染柱状图
const renderBarChart = () => {
  if (!barChartRef.value) return
  if (barChart) barChart.dispose()
  barChart = echarts.init(barChartRef.value)
  barChart.setOption({
    tooltip: { 
      trigger: 'axis', 
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const point = params[0]
        return `${point.name}<br/>营业额: ${point.value.toFixed(1)} 元`
      }
    },
    xAxis: { type: 'category', data: barData.value.map(item => item?.xItem), name: '日期' },
    yAxis: { type: 'value', name: '营业额 (元)' },
    series: [{
      name: '营业额',
      type: 'bar',
      data: barData.value.map(item => item?.yItem),
      itemStyle: { borderRadius: [4, 4, 0, 0], color: '#409EFF' }
    }]
  })
  barChart.off('click').on('click', (params) => {
    selectedBar.value = params.name
    if (!pieData.value || pieData.value.length === 0) {
      ElMessage.info('无销售数据')
      return
    }
    // 饼图dialog显示
    pieDataType.value = 'amount'
    pieDialogVisible.value = true
    // dialog打开后,pieChartRef.value再取得
    setTimeout(()=>{
      showPieChart()
    }, 100)
  })
}

// 显示饼图弹窗
const showPieChart = () => {
  if (!pieChartRef.value) return
  if (pieChart) pieChart.dispose()
  pieChart = echarts.init(pieChartRef.value)
  pieChart.setOption({
    tooltip: { 
      trigger: 'item',
      formatter: (params) => {
        return `${params.name}<br/>${params.value}${pieDataType.value === 'quantity' ? '份' : '元'} (${params.percent.toFixed(1)}%)`
      }
    },
    //legend: { orient: 'vertical', left: 'left' },
    series: [{
      name: '销售占比',
      type: 'pie',
      radius: '70%',
      data: pieData.value,
      emphasis: { scale: true },
      label: { show: true, formatter: '{b}: {d}%' }
    }]
  })
}

// 区间营业额取得
const getRevenue = async () => {
  if (!dateRange.value || dateRange.value.length !== 2) {
    ElMessage.warning('请选择日期区间')
    return
  }
  await statisticsStore.getPeriodRevenue(dateRange.value[0], dateRange.value[1])
  renderBarChart()
}

// unit变更
const unitChange = () => {
  statisticsStore.setUnitType(periodUnit)
  renderBarChart()
}

const onPieDataTypeChange = () => {
  showPieChart()
}

onMounted(() => {
  statisticsStore.setUnitType(periodUnit)
  getRevenue()
})

onUnmounted(() => {
  barChart?.dispose()
  pieChart?.dispose()
})
</script>

<style scoped>
.statistics-manage {
  width: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.card-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  padding: 0 2px;
}

.chart-area {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
}

.bar-chart {
  width: 100%;
  height: 400px;
}

.pie-chart {
  width: 100%;
  height: 400px;
}

.pie-toolbar {
  margin-bottom: 16px;
  text-align: center;
}
</style>